import { formatBaht } from '@xver/utils/number';
import { Loader } from '@mantine/core';
import { useTranslation } from 'react-i18next';

import type { BillableProduct, CreditSummaryResponse } from '@xver/@types/bill';
import DialogBox from '@xver/components/Atoms/DialogBox/DialogBox';

interface SpendByProductSectionProps {
    summary?: CreditSummaryResponse;
    isLoading: boolean;
    isError: boolean;
}

const PRODUCT_ORDER: BillableProduct[] = [
    'instance',
    'container',
    'registry',
    'backup',
    'baremetal',
    'bandwidth',
];

export default function SpendByProductSection({
    summary,
    isLoading,
    isError,
}: SpendByProductSectionProps) {
    const { t } = useTranslation('billings');

    const total = summary?.spend.total ?? 0;

    return (
        <DialogBox
            label={t('creditSummary.spend.title')}
            description={t('creditSummary.spend.description')}
            rightDescription={
                summary ? (
                    <span className="text-sm text-fg/68">
                        {t('creditSummary.spend.total')}{' '}
                        <span className="font-semibold text-fg">{formatBaht(total)} ฿</span>
                    </span>
                ) : undefined
            }
            classNames="p-4"
        >
            {isError ? (
                <div className="w-full flex items-center justify-center py-8 text-sm text-fg/68">
                    {t('creditSummary.error')}
                </div>
            ) : isLoading || !summary ? (
                <div className="w-full flex items-center justify-center py-8">
                    <Loader size="sm" color="var(--fg)" />
                </div>
            ) : (
                <div className="w-full flex flex-col gap-3">
                    {PRODUCT_ORDER.map((product) => {
                        const amount = summary.spend.byProduct[product] ?? 0;
                        const ratio = total > 0 ? amount / total : 0;
                        return (
                            <div key={product} className="w-full flex flex-col gap-1">
                                <div className="w-full flex flex-row justify-between items-center text-sm">
                                    <span className="text-fg/80">
                                        {t(`creditSummary.products.${product}`)}
                                    </span>
                                    <span className="font-semibold">{formatBaht(amount)} ฿</span>
                                </div>
                                <div className="w-full h-1.5 rounded bg-surface-3/60 overflow-hidden">
                                    <div
                                        className="h-full rounded bg-fg/70"
                                        style={{ width: `${Math.round(ratio * 100)}%` }}
                                    />
                                </div>
                            </div>
                        );
                    })}
                </div>
            )}
        </DialogBox>
    );
}
